import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { thCls, inputCls } from "./constants";

export function CouponsTab({ coupons, onChanged }) {
  const [form, setForm] = useState({ code: "", type: "percent", value: "", description: "" });

  const create = async () => {
    if (!form.code || !form.value) { toast.error("Code and value are required"); return; }
    try {
      await api.post("/admin/coupons", { ...form, value: Number(form.value) });
      toast.success("Coupon created");
      setForm({ code: "", type: "percent", value: "", description: "" });
      onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); onChanged(); };
  const remove = async (c) => { await api.delete(`/admin/coupons/${c.id}`); onChanged(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="rounded-[14px] border border-[#E4E9F0] bg-white p-6 space-y-4" style={{ boxShadow: "0 3px 12px rgba(15,23,42,0.035)" }}>
        <h3 className="font-serif-display text-2xl text-[#101828]">New coupon</h3>
        <div><Label className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]">Code</Label><Input data-testid="coupon-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} placeholder="SUMMER20" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="coupon-type" className="mt-1 rounded-xl border-[#E4E9F0]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]">Value</Label><Input data-testid="coupon-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} /></div>
        </div>
        <div><Label className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]">Description</Label><Input data-testid="coupon-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></div>
        <Button data-testid="coupon-create" className="w-full rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white" onClick={create}>Create coupon</Button>
      </div>

      <div className="lg:col-span-2 rounded-[14px] border border-[#E4E9F0] bg-white overflow-hidden" style={{ boxShadow: "0 3px 12px rgba(15,23,42,0.035)" }}>
        <Table>
          <TableHeader><TableRow><TableHead className={thCls}>Code</TableHead><TableHead className={thCls}>Discount</TableHead><TableHead className={thCls}>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {coupons.map((c) => (
              <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
                <TableCell className="font-mono font-medium text-[#101828]">{c.code}<div className="text-xs text-[#94A3B8] font-sans">{c.description}</div></TableCell>
                <TableCell className="font-mono text-[#475467]">{c.type === "percent" ? `${c.value}%` : `$${c.value}`}</TableCell>
                <TableCell><button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${c.active ? "bg-[#E5F7ED] text-[#087443]" : "bg-[#F1F2F4] text-[#51565F]"}`}>{c.active ? "Active" : "Inactive"}</button></TableCell>
                <TableCell className="text-right"><Button data-testid={`coupon-delete-${c.code}`} variant="ghost" size="sm" onClick={() => remove(c)}><Trash2 className="h-4 w-4 text-red-500" strokeWidth={1.5} /></Button></TableCell>
              </TableRow>
            ))}
            {coupons.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#94A3B8] py-10">No coupons yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
